import type { Lang } from "./index";

// Landing-page copy in all three shipped languages. Each language is
// written natively (grammar, idiom, tone) — not a literal translation.

type Card = { title: string; body: string };

export type LpCopy = {
  navSignIn: string;
  navSignUp: string;
  referralBanner: (code: string) => string;
  heroEyebrow: string;
  heroLine1: string;
  heroLine2: string;
  heroLine3: string;
  heroSub: string;
  ctaPrimary: string;
  ctaSecondary: string;
  heroFinePrint: string;
  featuresEyebrow: string;
  features: [Card, Card, Card, Card, Card, Card];
  problemEyebrow: string;
  problemH2: string;
  problemList: string[];
  solutionEyebrow: string;
  solutionH2: string;
  solutionList: string[];
  betaEyebrow: string;
  betaH2: string;
  betaBody: string;
  betaCta: string;
  betaFinePrint: string;
  howEyebrow: string;
  howH2: string;
  steps: [Card, Card, Card];
  finalEyebrow: string;
  finalLine1: string;
  finalLine2: string;
  finalCta: string;
  finalFinePrint: string;
  footerPrivacy: string;
  footerTerms: string;
  madeIn: string;
};

const pl: LpCopy = {
  navSignIn: "Zaloguj się",
  navSignUp: "Załóż konto",
  referralBanner: (code) =>
    `Wchodzisz z polecenia · kod ${code} jest już aktywny przy rejestracji`,
  heroEyebrow: "Kalorie bez matematyki · zamknięta beta",
  heroLine1: "Zrób zdjęcie.",
  heroLine2: "Opisz słowami.",
  heroLine3: "Resztę policzymy",
  heroSub:
    "Sytofood to licznik kalorii bez tabelek. Syto Sense rozpoznaje posiłki ze zdjęcia, układa przepisy pod twój dzienny limit i czyta karty restauracyjne w obcym języku.",
  ctaPrimary: "Załóż konto za darmo →",
  ctaSecondary: "Mam już konto",
  heroFinePrint:
    "Bez karty. Bez reklam. Twoje zdjęcia trafiają do Syto Sense dopiero, gdy sam się na to zgodzisz.",
  featuresEyebrow: "Co potrafi Sytofood",
  features: [
    {
      title: "Mam ochotę na…",
      body: "Wpisujesz, na co masz ochotę — na przykład „kanapka z łososiem”. Syto Sense układa przepis pod twój dzisiejszy limit, sprawdza lodówkę i dorzuca brakujące produkty na listę zakupów.",
    },
    {
      title: "Karta restauracji",
      body: "Robisz zdjęcie menu w obcym języku. W kilka sekund masz tłumaczenie i trzy najlżejsze pozycje z kaloriami i białkiem.",
    },
    {
      title: "Zdjęcie talerza",
      body: "Fotografujesz posiłek. Syto Sense rozkłada go na składniki, a ty potwierdzasz ilości. Bez wagi, bez wpisywania.",
    },
    {
      title: "Wirtualna lodówka",
      body: "Skanujesz raz, a ona pamięta wszystko. Przypomni o produktach na wylocie i zaproponuje dania z tego, co masz w domu.",
    },
    {
      title: "Głos i kod kreskowy",
      body: "Nie chce ci się pisać? Powiedz na głos. Masz batonik? Zeskanuj kod. Cztery sposoby logowania, jeden licznik.",
    },
    {
      title: "Cel dnia, nie kara",
      body: "Pokazujemy, ile ci zostało, a nie ile przekroczyłeś. Bez moralizowania, bez czerwonych liczb, bez krzykliwych powiadomień.",
    },
  ],
  problemEyebrow: "Tak być nie musi",
  problemH2: "Inne aplikacje zabierają dwadzieścia minut dziennie",
  problemList: [
    "Bazy z tysiącami produktów, których nikt nie używa",
    "Ważenie każdej kromki chleba na kuchennej wadze",
    "Wykresy z podpisem „eksperci zalecają”",
    "Reklamy suplementów i programów odchudzających",
    "Powiadomienia, które przypominają o jedzeniu z poczuciem winy",
  ],
  solutionEyebrow: "W Sytofood",
  solutionH2: "Licznik, który zostawia cię w spokoju",
  solutionList: [
    "Zalogowanie posiłku zajmuje 5 sekund: zdjęcie, klik, gotowe",
    "Cztery sposoby dodawania: zdjęcie, opis, głos, kod kreskowy",
    "Lodówka pamięta zapasy i ostrzega przed datą ważności",
    "Tłumaczenie karty restauracyjnej w obcym języku",
    "Bez reklam i bez „kup nasz program za 199 zł”",
  ],
  betaEyebrow: "Zamknięta beta",
  betaH2: "Wchodzisz teraz, korzystasz za darmo",
  betaBody:
    "Cennik ustalimy razem ze społecznością po becie. Pierwsi użytkownicy zachowają cenę founderską na zawsze.",
  betaCta: "Dołącz do bety →",
  betaFinePrint: "Wejście tylko z kodem zaproszeniowym",
  howEyebrow: "Jak to działa",
  howH2: "Trzy kroki",
  steps: [
    {
      title: "Załóż konto",
      body: "Email, hasło i kod zaproszeniowy od znajomego (albo nasz). Trzydzieści sekund.",
    },
    {
      title: "Ustaw cel",
      body: "Wzrost, waga, aktywność — Sytofood policzy, ile kalorii potrzebujesz. Możesz to nadpisać ręcznie.",
    },
    {
      title: "Loguj posiłki",
      body: "Zdjęcie, opis, głos albo kod kreskowy. To, co akurat wygodniejsze.",
    },
  ],
  finalEyebrow: "Zaczynamy",
  finalLine1: "Twój pierwszy posiłek",
  finalLine2: "w pięć sekund",
  finalCta: "Załóż konto →",
  finalFinePrint:
    "Bez karty. W becie za darmo. Konto i dane usuniesz dwoma kliknięciami.",
  footerPrivacy: "Polityka prywatności",
  footerTerms: "Regulamin",
  madeIn: "Zaprojektowane i zbudowane w Polsce",
};

const en: LpCopy = {
  navSignIn: "Sign in",
  navSignUp: "Sign up",
  referralBanner: (code) =>
    `You're here on a referral · code ${code} is already active at sign-up`,
  heroEyebrow: "Calories without the math · closed beta",
  heroLine1: "Snap a photo.",
  heroLine2: "Say it out loud.",
  heroLine3: "We'll do the rest",
  heroSub:
    "Sytofood is a calorie counter without the spreadsheets. Syto Sense recognises meals from a photo, builds recipes around your daily limit, and reads restaurant menus in any language.",
  ctaPrimary: "Create a free account →",
  ctaSecondary: "I already have an account",
  heroFinePrint:
    "No card. No ads. Your photos only reach Syto Sense once you opt in.",
  featuresEyebrow: "What Sytofood does",
  features: [
    {
      title: "I feel like…",
      body: "Type whatever you're craving — say, a salmon sandwich. Syto Sense builds a recipe within today's limit, checks your fridge, and adds what's missing to your shopping list.",
    },
    {
      title: "Restaurant menu",
      body: "Photograph a menu in a foreign language. In seconds you get a translation and the three lightest dishes with calories and protein.",
    },
    {
      title: "Photo of your plate",
      body: "Photograph your meal. Syto Sense breaks it into ingredients and you confirm the amounts. No scale, no typing.",
    },
    {
      title: "Virtual fridge",
      body: "Scan once and it remembers everything. It flags items about to expire and suggests dishes from what you already have.",
    },
    {
      title: "Voice and barcode",
      body: "Don't feel like typing? Say it out loud. Got a snack bar? Scan the code. Four ways to log, one counter.",
    },
    {
      title: "A daily goal, not a punishment",
      body: "We show what you have left, not how far you went over. No lecturing, no red numbers, no nagging notifications.",
    },
  ],
  problemEyebrow: "It doesn't have to be this way",
  problemH2: "Other apps take twenty minutes a day",
  problemList: [
    "Databases with thousands of products nobody uses",
    "Weighing every slice of bread on a kitchen scale",
    "Charts captioned “experts recommend”",
    "Ads for supplements and weight-loss programmes",
    "Notifications that remind you to eat with a side of guilt",
  ],
  solutionEyebrow: "With Sytofood",
  solutionH2: "A counter that leaves you alone",
  solutionList: [
    "Logging a meal takes 5 seconds: photo, tap, done",
    "Four ways to add: photo, description, voice, barcode",
    "The fridge remembers your stock and warns before expiry",
    "Restaurant menu translation in any language",
    "No ads, no “buy our programme for 199 zł”",
  ],
  betaEyebrow: "Closed beta",
  betaH2: "Join now, use it free",
  betaBody:
    "We'll set pricing together with the community after the beta. Early users keep a founder price forever.",
  betaCta: "Join the beta →",
  betaFinePrint: "Entry by invite code only",
  howEyebrow: "How it works",
  howH2: "Three steps",
  steps: [
    {
      title: "Create an account",
      body: "Email, password and an invite code from a friend (or ours). Thirty seconds.",
    },
    {
      title: "Set your goal",
      body: "Height, weight, activity — Sytofood works out how many calories you need. You can override it manually.",
    },
    {
      title: "Log your meals",
      body: "Photo, description, voice or barcode. Whatever's easiest in the moment.",
    },
  ],
  finalEyebrow: "Let's start",
  finalLine1: "Your first meal",
  finalLine2: "in five seconds",
  finalCta: "Create an account →",
  finalFinePrint:
    "No card. Free during the beta. Delete your account and data in two clicks.",
  footerPrivacy: "Privacy policy",
  footerTerms: "Terms",
  madeIn: "Designed and built in Poland",
};

const de: LpCopy = {
  navSignIn: "Anmelden",
  navSignUp: "Konto erstellen",
  referralBanner: (code) =>
    `Du kommst über eine Empfehlung · Code ${code} ist bei der Registrierung bereits aktiv`,
  heroEyebrow: "Kalorien ohne Rechnerei · geschlossene Beta",
  heroLine1: "Mach ein Foto.",
  heroLine2: "Sag es einfach.",
  heroLine3: "Den Rest rechnen wir",
  heroSub:
    "Sytofood ist ein Kalorienzähler ohne Tabellen. Syto Sense erkennt Mahlzeiten auf dem Foto, stellt Rezepte für dein Tageslimit zusammen und liest Speisekarten in jeder Sprache.",
  ctaPrimary: "Kostenlos registrieren →",
  ctaSecondary: "Ich habe schon ein Konto",
  heroFinePrint:
    "Keine Karte. Keine Werbung. Deine Fotos gehen erst an Syto Sense, wenn du zustimmst.",
  featuresEyebrow: "Was Sytofood kann",
  features: [
    {
      title: "Ich habe Lust auf…",
      body: "Tippe ein, worauf du Lust hast — zum Beispiel ein Lachsbrötchen. Syto Sense baut ein Rezept innerhalb deines heutigen Limits, prüft deinen Kühlschrank und setzt Fehlendes auf die Einkaufsliste.",
    },
    {
      title: "Speisekarte",
      body: "Fotografiere eine Speisekarte in einer fremden Sprache. In Sekunden bekommst du eine Übersetzung und die drei leichtesten Gerichte mit Kalorien und Eiweiß.",
    },
    {
      title: "Foto vom Teller",
      body: "Fotografiere deine Mahlzeit. Syto Sense zerlegt sie in Zutaten und du bestätigst die Mengen. Keine Waage, kein Tippen.",
    },
    {
      title: "Virtueller Kühlschrank",
      body: "Einmal scannen, und er merkt sich alles. Er weist auf bald ablaufende Produkte hin und schlägt Gerichte aus dem vor, was du zu Hause hast.",
    },
    {
      title: "Sprache und Barcode",
      body: "Keine Lust zu tippen? Sag es laut. Einen Riegel dabei? Scanne den Code. Vier Wege zum Erfassen, ein Zähler.",
    },
    {
      title: "Ein Tagesziel, keine Strafe",
      body: "Wir zeigen, was dir bleibt, nicht wie weit du drüber bist. Keine Belehrungen, keine roten Zahlen, keine nervigen Benachrichtigungen.",
    },
  ],
  problemEyebrow: "Es muss nicht so sein",
  problemH2: "Andere Apps kosten zwanzig Minuten am Tag",
  problemList: [
    "Datenbanken mit Tausenden Produkten, die niemand nutzt",
    "Jede Scheibe Brot auf der Küchenwaage wiegen",
    "Diagramme mit der Unterschrift „Experten empfehlen“",
    "Werbung für Nahrungsergänzung und Abnehmprogramme",
    "Benachrichtigungen, die mit schlechtem Gewissen ans Essen erinnern",
  ],
  solutionEyebrow: "Mit Sytofood",
  solutionH2: "Ein Zähler, der dich in Ruhe lässt",
  solutionList: [
    "Eine Mahlzeit zu erfassen dauert 5 Sekunden: Foto, Tippen, fertig",
    "Vier Wege zum Hinzufügen: Foto, Beschreibung, Sprache, Barcode",
    "Der Kühlschrank merkt sich Vorräte und warnt vor dem Ablaufdatum",
    "Übersetzung der Speisekarte in jeder Sprache",
    "Keine Werbung, kein „Kauf unser Programm für 199 zł“",
  ],
  betaEyebrow: "Geschlossene Beta",
  betaH2: "Jetzt einsteigen, gratis nutzen",
  betaBody:
    "Den Preis legen wir nach der Beta gemeinsam mit der Community fest. Frühe Nutzer behalten dauerhaft einen Gründerpreis.",
  betaCta: "Zur Beta →",
  betaFinePrint: "Zugang nur mit Einladungscode",
  howEyebrow: "So funktioniert's",
  howH2: "Drei Schritte",
  steps: [
    {
      title: "Konto erstellen",
      body: "E-Mail, Passwort und ein Einladungscode von Freunden (oder von uns). Dreißig Sekunden.",
    },
    {
      title: "Ziel festlegen",
      body: "Größe, Gewicht, Aktivität — Sytofood berechnet, wie viele Kalorien du brauchst. Du kannst es manuell überschreiben.",
    },
    {
      title: "Mahlzeiten erfassen",
      body: "Foto, Beschreibung, Sprache oder Barcode. Was gerade am einfachsten ist.",
    },
  ],
  finalEyebrow: "Los geht's",
  finalLine1: "Deine erste Mahlzeit",
  finalLine2: "in fünf Sekunden",
  finalCta: "Konto erstellen →",
  finalFinePrint:
    "Keine Karte. Während der Beta kostenlos. Konto und Daten in zwei Klicks löschbar.",
  footerPrivacy: "Datenschutz",
  footerTerms: "AGB",
  madeIn: "Entworfen und gebaut in Polen",
};

export const lpCopy: Record<Lang, LpCopy> = { pl, en, de };
